import { ChangeEvent, FC, Fragment, useState, useCallback, useEffect } from 'react';
import { debounce } from 'throttle-debounce';
import { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
// Types
import CaseStatuses from 'app/types/CaseStatuses';
import { Dimension } from 'app/store/BillingUsage/BillingUsage.types';
// Store
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Async
import { getInsuranceCases } from 'app/store/Cases/Cases.async';
// Actions
import { BillingUsageActions } from 'app/store/BillingUsage/BillingUsage.slice';
// Selectors
import { selectAccountsAsOptions } from 'app/store/Clients/Clients.selectors';
import { selectActiveSubscriptionsAsOptions, selectActiveSubscriptionServicesAsOptions } from 'app/store/BillingSubscriptions/BillingSubscriptions.selectors';
import { selectCaseOptions } from 'app/store/Cases/Cases.selectors';
import { selectParams, selectValidateDateRange } from 'app/store/BillingUsage/BillingUsage.selectors';
// Mui
import {
  Theme, Drawer, Typography, CircularProgress,
  Autocomplete as MuiAutocomplete, TextField,
  Divider,
  FormHelperText
} from '@mui/material';
// Components
import { DesktopDatePicker, Select } from 'app/components/Mui';
import { Autocomplete } from 'app/components/Mui/Autocompletes';
import QuickDateChips from 'app/components/QuickDateChips';
// Constant
import { DATA_LIMIT, SIDEBAR_WIDTH } from 'app/App.constants';
// Utilities
import { adjustDateRange } from 'app/utilities/DateTime';
// Mixpanel
import MixpanelActions from 'app/features/mixpanel/MixpanelActions';

const CostExplorerDrawer:FC = () => {
  const { t } = useTranslation();
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const accountOptions = useAppSelector(selectAccountsAsOptions);
  const subscriptionOptions = useAppSelector(selectActiveSubscriptionsAsOptions);
  const serviceOptions = useAppSelector(selectActiveSubscriptionServicesAsOptions);
  const caseOptions = useAppSelector(selectCaseOptions);
  const params = useAppSelector(selectParams);
  const validationErrors = useAppSelector(selectValidateDateRange);

  const [ stateParams, setStateParams ] = useState(params);

  const formatDate = (date:Dayjs | null, period:'start' | 'end') => {
    if (!date) return null;
    return date.add(period === 'start' ? 0 : 1, 'day').format('YYYY-MM-DD');
  }

  // eslint-disable-next-line
  const debouncePeriodParams = useCallback(debounce(1000, (period: 'start' | 'end', value: Dayjs | null, oppositeValue: Dayjs | null) => {
    const { start, end, allowStateUpdate } = adjustDateRange(period, value, oppositeValue);

    if ( allowStateUpdate ) {
      setStateParams((prevState:any) => ({ ...prevState, 'period.start': start, 'period.end': end }));
    }

    dispatch(MixpanelActions.handleCostExplorerDateFilterClick({ start: formatDate(start, 'start'), end: formatDate(end, 'end') }));

    dispatch(BillingUsageActions.setParams({ 'period.start': start, 'period.end': end }));
    // eslint-disable-next-line
  }), []);

  // eslint-disable-next-line
  const debounceGetCases = useCallback(debounce(500, (search:string) => {
    dispatch(getInsuranceCases({
      // ...caseParams,
      offset: 0,
      limit: DATA_LIMIT,
      search,
      status: `${CaseStatuses.Open},${CaseStatuses.Archived}`
    }))
  }), []);

  useEffect(() => {
    debounceGetCases('');
    // eslint-disable-next-line
  }, []);

  const handleMixpanelEvent = (field:keyof typeof params, value:any) => {
    if (field === 'dimensions') {
      dispatch(MixpanelActions.handleCostExplorerBreakdownSwitchClick({ breakdown: value }));
    }
    if (field === 'reference:clientAccount') {
      dispatch(MixpanelActions.handleCostExplorerClientFilterClick({ clientAccountId: value }));
    }
    if (field === 'subscriptionId') {
      dispatch(MixpanelActions.handleCostExplorerSubscriptionFilterClick({ subscriptionId: value }));
    }
    if (field === 'serviceId') {
      dispatch(MixpanelActions.handleCostExplorerServiceFilterClick({ serviceId: value }));
    }
    if (field === 'reference:case') {
      const caseId = value ? value.id : null;
      dispatch(MixpanelActions.handleCostExplorerCaseFilterClick({ caseId }));
    }
  }

  const handleChange = (field:keyof typeof params) => (newValue:any) => {
    dispatch(BillingUsageActions.setParams({ [field]: newValue }));
    handleMixpanelEvent(field, newValue);
  }

  const handleDateChange = (period:'start' | 'end') => (newValue:Dayjs | null) => {
    setStateParams((prevState:any) => {
      const oppositeValue = period === 'start' ? prevState['period.end'] : prevState['period.start'];
      debouncePeriodParams(period, newValue, oppositeValue);
      return {
        ...prevState,
        [`period.${period}`]: newValue
      };
    });
  };

  const handleDateRangeChange = (start: Dayjs | null, end: Dayjs | null) => {
    setStateParams({ ...stateParams, 'period.start': start, 'period.end': end });
    dispatch(BillingUsageActions.setParams({ 'period.start': start, 'period.end': end }));
    dispatch(MixpanelActions.handleCostExplorerDateFilterClick({ start: formatDate(start, 'start'), end: formatDate(end, 'end') }));
  };

  const handleInputChange = (_:any, value:string) => {
    debounceGetCases(value);
  }

  const initialCaseOptions = caseOptions || [];
  const caseLoading = !initialCaseOptions;

  return (
    <Drawer
      PaperProps={{
        sx: {
          width: SIDEBAR_WIDTH,
          position: 'relative',
          borderRadius: 0,
          border: 'initial',
          borderRight: (theme:Theme) => `1px solid ${theme.palette.divider}`,
          py: 2,
          px: 4
        }
      }}
      // Data attribute which exclude block from print using CTRL/CMD + P
      data-print="exclude"
      open={true}
      variant="permanent"
      anchor="left"
    >
      <Select
        label={t('labels.breakdown')} name="status" value={params.dimensions}
        onChange={(event:ChangeEvent<HTMLInputElement>) => handleChange('dimensions')(event.target.value)}
        options={[
          { id: Dimension.Service, name: t('labels.service') },
          { id: Dimension.Case, name: t('labels.case') },
          { id: Dimension.CaseService, name: t('labels.caseAndService') },
          { id: Dimension.SubscriptionServiceInvoice, name: t('labels.subscriptionAndServiceAndInvoice') }
        ]}
      />
      <Divider sx={{ mt: 4, mb: 2 }} />
      <Autocomplete
        value={params['reference:clientAccount']}
        onChange={handleChange('reference:clientAccount')}
        options={accountOptions || []}
        TextFieldProps={{
          label: t('labels.clientAccount')
        }}
        renderOption={(props, option) => (
          <li {...props} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }} key={option.value}>
            <Typography variant="subtitle1">{option.label}</Typography>
            {option.props.address ? (
              <Typography variant="caption">{option.props.address}</Typography>
            ) : null}
          </li>
        )}
      />
      <Autocomplete
        value={params.subscriptionId}
        onChange={handleChange('subscriptionId')}
        options={subscriptionOptions}
        TextFieldProps={{
          label: t('labels.subscription')
        }}
      />
      <Autocomplete
        value={params.serviceId}
        onChange={handleChange('serviceId')}
        options={serviceOptions}
        TextFieldProps={{
          label: t('labels.service')
        }}
      />
      <MuiAutocomplete
        value={params['reference:case'] as any}
        onChange={(_:any, newValue:any) => handleChange('reference:case')(newValue)}
        onInputChange={handleInputChange}
        options={caseOptions || []}
        loading={caseLoading}
        fullWidth
        renderInput={(params:any) => (
          <TextField
            {...params}
            label={t('form.placeholder.findCase')}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <Fragment>
                    {caseLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </Fragment>
                ),
              }
            }}
            margin="normal"
            fullWidth
          />
        )}
        renderOption={(props:any, option:any) => (
          <li {...props} key={option.id}>
            {option.name}
          </li>
        )}
        getOptionLabel={(option:any) => option.name}
        isOptionEqualToValue={(option:any, value:any) => option.id === value.id}
      />
      <Divider sx={{ mt: 2, mx: -4 }} />
      <DesktopDatePicker
        label={t('labels.start')} name="dateRangeStart" value={stateParams['period.start']}
        onChange={handleDateChange('start')}
        slotProps={{
          textField: {
            error: Boolean(validationErrors && validationErrors.startError),
            helperText: validationErrors?.startError || ''
          }
        }}
      />
      <DesktopDatePicker
        label={t('labels.end')} name="dateRangeEnd" value={stateParams['period.end']}
        onChange={handleDateChange('end')}
        slotProps={{
          textField: {
            error: Boolean(validationErrors && validationErrors.endError),
            helperText: validationErrors?.endError || ''
          }
        }}
      />
      {validationErrors && validationErrors.rangeError ? (
        <FormHelperText error>{validationErrors.rangeError}</FormHelperText>
      ) : null}
      <QuickDateChips onChange={handleDateRangeChange} />
    </Drawer>
  )
}

export default CostExplorerDrawer